import "./Home.scss";
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

// Icons
import {
  FaCalendar,
  FaPlay,
  FaArrowRight,
  FaPhone,
  FaBolt,
  FaClipboardCheck,
  FaDraftingCompass,
  FaRocket,
  FaTools,
  FaCheck,
  FaUsers,
  FaChartLine,
  FaWallet,
  FaGraduationCap,
  FaCubes,
  FaComments,
  FaExternalLinkAlt,
} from "react-icons/fa";

import {
  HiMiniUserGroup,
  HiCommandLine,
  HiMiniRocketLaunch,
  HiMiniClipboardDocumentList,
  HiMiniCheckBadge,
} from "react-icons/hi2";

import heroImg from "../../assets/images/heroImg.png";
import form from "../../assets/images/form.png";
import fee from "../../assets/images/fee.png";
import timetable from "../../assets/images/timetable.png";
import students from "../../assets/images/students.png";
import library from "../../assets/images/library.png";
import visa from "../../assets/images/visa.png";

// Components
import Button from "../../components/Button/Button";
import VideoModal from "../../components/VideoModal/VideoModal";

const Home = () => {
  const navigate = useNavigate();
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    const tokens = JSON.parse(localStorage.getItem("ERPTokens"));
    if (tokens) {
      const decodedToken = jwtDecode(tokens.accessToken);
      const role_id = decodedToken?.role_id;

      if (role_id === 10) {
        navigate("/student");
      } else {
        navigate("/admin");
      }
    }
  };

  const features = [
    {
      icon: form,
      title: "Smart Digital Admissions",
      content:
        "Streamline admissions with automated workflows and digital forms for error-free enrollment.",
      color: "#3B82F6",
    },
    {
      icon: fee,
      title: "Intelligent Fee Management",
      content:
        "Automated invoicing, real-time tracking, and secure payment processing all in one place.",
      color: "#8B5CF6",
    },
    {
      icon: timetable,
      title: "Dynamic Scheduling",
      content:
        "AI-powered timetable management that prevents conflicts and optimizes resource allocation.",
      color: "#22C55E",
    },
    {
      icon: students,
      title: "360° Student Management",
      content:
        "Complete student lifecycle management from admission to alumni relations.",
      color: "#F97316",
    },
    {
      icon: library,
      title: "Smart Resource Control",
      content:
        "Digital library management with automated tracking and smart inventory alerts.",
      color: "#EC4899",
    },
    {
      icon: visa,
      title: "Secure Data Hub",
      content:
        "Enterprise-grade security with role-based access and real-time data synchronization.",
      color: "#6366F1",
    },
  ];

  const processes = [
    {
      number: "01",
      title: "Digital-First Admissions",
      content:
        "Transform your admission process with smart forms, automated workflows, and instant updates for seamless student onboarding.",
      icon: <FaUsers />,
    },
    {
      number: "02",
      title: "Intelligent Processing",
      content:
        "Automated test evaluation, instant result publishing, and streamlined enrollment management with advanced analytics.",
      icon: <FaChartLine />,
    },
    {
      number: "03",
      title: "Smart Fee Management",
      content:
        "Automated billing cycles, multiple payment gateway integrations, and real-time financial tracking with detailed reports.",
      icon: <FaWallet />,
    },
    {
      number: "04",
      title: "Academic Excellence Tools",
      content:
        "Comprehensive academic planning, curriculum management, and performance tracking to ensure educational success.",
      icon: <FaGraduationCap />,
    },
    {
      number: "05",
      title: "Resource Optimization",
      content:
        "Efficient management of infrastructure, library, inventory, and human resources through intelligent allocation systems.",
      icon: <FaCubes />,
    },
    {
      number: "06",
      title: "Unified Communications",
      content:
        "Streamlined communication channels between students, parents, teachers, and administration for better collaboration.",
      icon: <FaComments />,
    },
  ];

  return (
    <div className="home">
      <AnimatePresence>
        {/* Hero Section */}
        <motion.div
          className="home-hero"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="home-hero-content">
            <motion.div
              className="home-hero-content-badge"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              Lancehawks ERP
            </motion.div>

            <motion.div
              className="home-hero-content-title"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Transform Your Institution with Smart Management
            </motion.div>

            <motion.div
              className="home-hero-content-subtitle"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              A comprehensive ERP solution that brings efficiency to every
              aspect of your institution — from admissions to alumni, finance to
              facilities.
            </motion.div>

            <motion.div
              className="home-hero-content-buttons"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Button
                type="secondary"
                text="Request a Demo"
                icon={<FaCalendar />}
                onClick={() => navigate("/contact")}
              />
              <Button
                type="outline"
                text="Lancehawks"
                icon={<FaExternalLinkAlt />}
                onClick={() => window.open('https://lancehawks.com/', '_blank')}
              />
            </motion.div>

            <motion.div
              className="home-hero-stats"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="stat-item">
                <span className="number">5000+</span>
                <span className="label">Active Users</span>
              </div>
              <div className="stat-item">
                <span className="number">60+</span>
                <span className="label">Features</span>
              </div>
              <div className="stat-item">
                <span className="number">4+</span>
                <span className="label">Cities</span>
              </div>
            </motion.div>
          </div>

          <motion.div
            className="home-hero-image"
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 100 }}
          >
            <img src={heroImg} alt="ERP Dashboard Preview" />
          </motion.div>
        </motion.div>

        {/* Features Section */}
        <section className="home-features">
          <div className="section-header">
            <motion.span
              className="section-badge"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Features
            </motion.span>
            <motion.h2
              className="home-features-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Everything You Need in One Platform
            </motion.h2>
          </div>

          <div className="home-features-cards">
            {features.map((feature, index) => (
              <FeatureCard key={index} {...feature} index={index} />
            ))}
          </div>
        </section>

        {/* Process Section */}
        <section className="home-processes">
          <div className="section-header">
            <motion.span
              className="section-badge"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              How It Works
            </motion.span>
            <motion.h2
              className="home-processes-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Your Journey to Digital Transformation
            </motion.h2>
          </div>

          <div className="home-processes-cards">
            {processes.map((process, index) => (
              <ProcessCard key={index} {...process} index={index} />
            ))}
          </div>
        </section>

        {/* Implementation Process */}
        <ERPProcess />

        {/* CTA Section */}
        <motion.section
          className="home-contact"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="contact-card">
            <h2 className="home-contact-title">
              Ready to Transform Your Institution?
            </h2>
            <p className="home-contact-subtitle">
              Join hundreds of institutions that have revolutionized their
              operations with Lancehawks ERP
            </p>
            <div className="cta-buttons">
              <Button
                type="secondary"
                text="Get Started"
                icon={<FaArrowRight />}
                onClick={() => navigate("/contact")}
              />
              <Button
                type="secondary"
                text="Watch Video"
                icon={<FaPlay />}
                onClick={() => setIsVideoModalOpen(true)}
              />
              <Button
                type="outline"
                text="Talk to Sales"
                icon={<FaPhone />}
                onClick={() => (window.location.href = "tel:+919915697333")}
              />
            </div>
          </div>
        </motion.section>
      </AnimatePresence>
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="fDBV1MWQZnU"
      />
    </div>
  );
};

// Subcomponents
const FeatureCard = ({ icon, title, content, color, index }) => {
  return (
    <motion.div
      className="feature-card"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <div className="feature-card-meta">
        <div className="icon-container" style={{ backgroundColor: color }}>
          <img src={icon} alt={title} />
        </div>
        <h3 className="title">{title}</h3>
      </div>
      <div className="card-content">
        <p>{content}</p>
      </div>
    </motion.div>
  );
};

const ProcessCard = ({ number, title, content, icon, index }) => {
  return (
    <motion.div
      className="process-card"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <div className="process-card-meta">
        <div className="number">{number}</div>
        <div className="icon-wrapper">{icon}</div>
        <h3 className="title">{title}</h3>
      </div>
      <div className="card-content">
        <p>{content}</p>
      </div>
    </motion.div>
  );
};

export default Home;

const ERPProcess = () => {
  const navigate = useNavigate();
  const steps = [
    {
      icon: <HiMiniUserGroup className="w-8 h-8" />,
      title: "Understand Your Institute's Needs",
      subtitle: "Free Consultation",
      description:
        "We begin with a thorough analysis of your requirements, processes, and goals to ensure our solution perfectly aligns with your needs.",
      features: [
        "Requirements gathering",
        "Process analysis",
        "Goal setting",
        "Timeline planning",
      ],
      color: "#3B82F6",
    },
    {
      icon: <HiCommandLine className="w-8 h-8" />,
      title: "Design & Develop",
      subtitle: "Tailored ERP Solution",
      description:
        "Our team crafts a custom ERP solution that matches your workflow, incorporating all the features and integrations you need.",
      features: [
        "Custom development",
        "UI/UX design",
        "Integration planning",
        "Quality testing",
      ],
      color: "#8B5CF6",
    },
    {
      icon: <HiMiniRocketLaunch className="w-8 h-8" />,
      title: "Deploy & Train",
      subtitle: "Smooth Adoption",
      description:
        "We ensure a seamless deployment and provide comprehensive training to your staff for maximum efficiency and adoption.",
      features: [
        "System deployment",
        "Staff training",
        "Data migration",
        "User support",
      ],
      color: "#22C55E",
    },
    {
      icon: <HiMiniClipboardDocumentList className="w-8 h-8" />,
      title: "Ongoing Support",
      subtitle: "Future Enhancements",
      description:
        "Our commitment continues with dedicated support and regular updates to keep your ERP system evolving with your needs.",
      features: [
        "24/7 support",
        "Regular updates",
        "Performance monitoring",
        "System optimization",
      ],
      color: "#F97316",
    },
  ];

  return (
    <div className="steps">
      <div className="section-header">
        <motion.span
          className="section-badge"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          🛠 Our Process
        </motion.span>
        {/* <motion.h2
          className="home-features-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Everything You Need in One Platform
        </motion.h2> */}
      </div>
      <div className="steps__title">
        {/* 🛠 Our Process – */}
        How We Build Your Custom ERP
      </div>

      <div className="steps__subtitle">
        A systematic approach to transforming your institution with custom ERP
        solutions
      </div>
      <div className="steps__timeline">
        {steps.map((step, index) => (
          <div key={index} className="steps__step">
            <div
              className={`steps__step-content ${
                index % 2 === 0
                  ? "steps__step-content--left"
                  : "steps__step-content--right"
              }`}
            >
              <div className="steps__step-box">
                <div className="steps__step-box-meta">
                  <div className="steps__step-box-meta-number">
                    0{index + 1}
                  </div>
                  <div className="steps__step-box-meta-info">
                    <div className="steps__step-box-meta-info-title">
                      {step.title}
                    </div>
                    <div className="steps__step-box-meta-info-subtitle">
                      {step.subtitle}
                    </div>
                  </div>
                </div>
                <div className="steps__step-box-description">
                  {step.description}
                </div>

                <div className="steps__step-features">
                  {step.features.map((feature, idx) => (
                    <div key={idx} className="feature-item">
                      <div
                        className="icon"
                        style={{ backgroundColor: step.color }}
                      >
                        <HiMiniCheckBadge size={14} />
                      </div>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div
                className={`steps__step-icon steps__step-icon--${index + 1}`}
                style={{ backgroundColor: step.color }}
              >
                {step.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      <button className="cta-button" onClick={() => navigate("/contact")}>
        Let's Build Your ERP – Get Started Today
        <FaTools className="arrow-icon" size={20} />
      </button>
    </div>
  );
};
